import "./admin.page.css";

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchProfiles, updateRoles, updateStatus } from "../../clients/admin.client";
import { fetchUser } from "../../clients/auth.client";
import { changeProfile } from "../../reducers/auth.reducer";

const ROLES = ["PLAYER", "MASTER", "ADMIN"];

const AdminPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tokenValue, user } = useSelector((state) => state.auth);
  const [query, setQuery] = useState({ search: "", role: "", status: "", page: 0, size: 20 });
  const [draftSearch, setDraftSearch] = useState("");
  const [pageData, setPageData] = useState(null);
  const [state, setState] = useState("loading");
  const [errorStatus, setErrorStatus] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draftRoles, setDraftRoles] = useState([]);
  const [pendingId, setPendingId] = useState(null);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    const response = await fetchProfiles(query, tokenValue);
    if (response.isError) {
      setErrorStatus(response.status);
      setState("error");
      return;
    }
    setPageData(response.data);
    setState("ready");
  }, [query, tokenValue]);

  useEffect(() => { load(); }, [load]);

  const replaceRow = (updated) => {
    setPageData((current) => ({
      ...current,
      content: current.content.map((profile) => profile.id === updated.id ? updated : profile),
    }));
  };

  const refreshCurrentUser = async () => {
    const current = await fetchUser(tokenValue);
    if (current) dispatch(changeProfile(current));
  };

  const saveRoles = async (profile) => {
    if (draftRoles.length === 0) {
      setMessage(t("admin.rolesRequired"));
      return;
    }
    setPendingId(profile.id);
    setMessage("");
    const response = await updateRoles(profile.id, draftRoles, tokenValue);
    setPendingId(null);
    if (response.isError) {
      setMessage(response.status === 409 ? t("admin.conflict") : t("admin.updateError"));
      return;
    }
    replaceRow(response.data);
    setEditingId(null);
    if (profile.id === user.id) await refreshCurrentUser();
  };

  const toggleStatus = async (profile) => {
    const next = profile.status === "ACTIVE" ? "BLOCKED" : "ACTIVE";
    if (!window.confirm(t(next === "BLOCKED" ? "admin.confirmBlock" : "admin.confirmUnblock", { email: profile.email }))) return;
    setPendingId(profile.id);
    setMessage("");
    const response = await updateStatus(profile.id, next, tokenValue);
    setPendingId(null);
    if (response.isError) {
      setMessage(response.status === 409 ? t("admin.conflict") : t("admin.updateError"));
      return;
    }
    replaceRow(response.data);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setQuery((current) => ({ ...current, search: draftSearch.trim(), page: 0 }));
  };

  return (
    <main className="admin-page">
      <header><p>{t("admin.eyebrow")}</p><h1>{t("admin.title")}</h1><span>{t("admin.subtitle")}</span></header>
      <form className="admin-filters" onSubmit={submitSearch}>
        <label>{t("admin.search")}<input type="search" value={draftSearch} onChange={(e) => setDraftSearch(e.target.value)} /></label>
        <label>{t("admin.roleFilter")}<select value={query.role} onChange={(e) => setQuery({ ...query, role: e.target.value, page: 0 })}>
          <option value="">{t("admin.allRoles")}</option>{ROLES.map((role) => <option key={role} value={role}>{t(`admin.roles.${role}`)}</option>)}
        </select></label>
        <label>{t("admin.statusFilter")}<select value={query.status} onChange={(e) => setQuery({ ...query, status: e.target.value, page: 0 })}>
          <option value="">{t("admin.allStatuses")}</option><option value="ACTIVE">{t("admin.statuses.ACTIVE")}</option><option value="BLOCKED">{t("admin.statuses.BLOCKED")}</option>
        </select></label>
        <button type="submit">{t("admin.find")}</button>
      </form>

      <div className="admin-live" role="status" aria-live="polite">{message}</div>
      {state === "loading" && <p>{t("admin.loading")}</p>}
      {state === "error" && <section className="admin-state" role="alert"><h2>{errorStatus === 403 ? t("admin.forbidden") : t("admin.loadError")}</h2><button type="button" onClick={load}>{t("common.retry")}</button></section>}
      {state === "ready" && pageData?.content.length === 0 && <section className="admin-state"><h2>{t("admin.empty")}</h2><p>{t("admin.emptyHint")}</p></section>}
      {state === "ready" && pageData?.content.length > 0 && (
        <div className="admin-table-wrap"><table><thead><tr><th>{t("admin.email")}</th><th>{t("admin.rolesLabel")}</th><th>{t("admin.statusLabel")}</th><th>{t("admin.characters")}</th><th>{t("common.actions")}</th></tr></thead>
          <tbody>{pageData.content.map((profile) => {
            const isSelf = profile.id === user.id;
            const editing = editingId === profile.id;
            return <tr key={profile.id}><td>{profile.email}{isSelf && <small>{t("admin.you")}</small>}</td>
              <td>{editing ? <fieldset><legend>{t("admin.rolesLabel")}</legend>{ROLES.map((role) => <label key={role}><input type="checkbox" checked={draftRoles.includes(role)} disabled={isSelf && role === "ADMIN"} onChange={() => setDraftRoles((current) => current.includes(role) ? current.filter((item) => item !== role) : [...current, role])} />{t(`admin.roles.${role}`)}</label>)}</fieldset> : <div className="admin-badges">{profile.roles.map((role) => <span key={role}>{t(`admin.roles.${role}`)}</span>)}</div>}</td>
              <td><span className={`admin-status admin-status--${profile.status.toLowerCase()}`}>{t(`admin.statuses.${profile.status}`)}</span></td><td>{profile.characterCount}</td>
              <td><div className="admin-actions">{editing ? <><button type="button" disabled={pendingId === profile.id} onClick={() => saveRoles(profile)}>{t("common.save")}</button><button type="button" onClick={() => setEditingId(null)}>{t("common.cancel")}</button></> : <button type="button" onClick={() => { setEditingId(profile.id); setDraftRoles(profile.roles); }}>{t("admin.editRoles")}</button>}
                <button type="button" disabled={pendingId === profile.id || (isSelf && profile.status === "ACTIVE")} title={isSelf ? t("admin.selfBlockHint") : undefined} onClick={() => toggleStatus(profile)}>{profile.status === "ACTIVE" ? t("admin.block") : t("admin.unblock")}</button></div></td></tr>;
          })}</tbody></table></div>
      )}
      {state === "ready" && pageData && <nav className="admin-pagination" aria-label={t("admin.pagination")}><button type="button" disabled={pageData.first} onClick={() => setQuery({ ...query, page: query.page - 1 })}>{t("admin.previous")}</button><span>{t("admin.page", { current: pageData.number + 1, total: pageData.totalPages || 1 })}</span><button type="button" disabled={pageData.last} onClick={() => setQuery({ ...query, page: query.page + 1 })}>{t("admin.next")}</button></nav>}
    </main>
  );
};

export default AdminPage;
